import { Component, Input, OnChanges, OnInit, OnDestroy, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Subject, switchMap, take, takeUntil } from 'rxjs';
import { TranslocoModule } from '@ngneat/transloco';
import { PathEnum } from 'app/modules/shared/enums/pathEnum';
import { Subscription } from 'app/modules/shared/models/subscription';
import { SubscriptionService } from 'app/modules/partner/services/subscription.service';
import { PlanEnum } from 'app/modules/shared/enums/planEnum';
import { TrialEndPlanSelectionComponent } from 'app/modules/shared/components/trial-end-plan-selection/trial-end-plan-selection.component';
import { SpinnerService } from 'app/modules/shared/services/spinner.service';
import { BackendResponseKeys } from 'app/modules/shared/enums/backendResponseKeys.enum';
import { UserService } from 'app/core/user/user.service';
import { UpgradeService } from 'app/modules/shared/services/upgrade.service';

@Component({
    selector: 'trial-period',
    templateUrl: './trial-period.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [
        MatButtonModule,
        MatIconModule,
        MatTooltipModule,
        MatDialogModule,
        TranslocoModule
    ],
})
export class TrialPeriodComponent implements OnInit, OnChanges, OnDestroy {
    @Input() subscription: Subscription;

    private _unsubscribeAll: Subject<any> = new Subject<any>();
    private _trialEndDialogShown: boolean = false;

    /**
     * Constructor
     */
    constructor(
        private _router: Router,
        private _subscriptionService: SubscriptionService,
        private _matDialog: MatDialog,
        private _spinnerService: SpinnerService,
        private _userService: UserService,
        private _upgradeService: UpgradeService
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Check if subscription is in trial period
     */
    get isTrialPeriod(): boolean {
        return this.subscription && this.subscription.subscriptionStatusRefCode === 'TRL';
    }

    /**
     * Whether the trial has an end date. A trial forced by the team has none (it never ends by itself): no countdown
     */
    get hasTrialEndDate(): boolean {
        return !!this.subscription?.trialPeriodEndDate;
    }

    /**
     * Calculate remaining trial days
     */
    get trialDaysRemaining(): number {
        if (!this.subscription || !this.subscription.trialPeriodEndDate) {
            return 0;
        }

        const endDate = new Date(this.subscription.trialPeriodEndDate);
        const currentDate = new Date();
        const timeDifference = endDate.getTime() - currentDate.getTime();
        const daysDifference = Math.ceil(timeDifference / (1000 * 3600 * 24));

        return Math.max(0, daysDifference);
    }

    /**
     * Get trial text with proper pluralization
     */
    get trialText(): string {
        const days = this.trialDaysRemaining;
        if (days === 1) {
            return 'features.common.trial.remaining_day_singular';
        } else {
            return 'features.common.trial.remaining_days_plural';
        }
    }

    /**
     * Navigate to subscription settings
     */
    navigateToSubscription(): void {
        this._router.navigate([`/${PathEnum.APP}/${PathEnum.APP_PARTNER_SETTINGS}/${PathEnum.APP_PARTNER_SETTINGS_SUBSCRIPTION}`]);
    }

    /**
     * Open the plan selection modal directly
     */
    navigateToUpgrade(): void {
        this._upgradeService.openUpgradeModal();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // If no subscription is provided via input, subscribe to the service
        if (!this.subscription) {
            this._subscriptionService.partnerSubscription$
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe((subscription: Subscription) => {
                    this.subscription = subscription;
                    this.checkAndShowTrialEndDialog();
                });
        } else {
            this.checkAndShowTrialEndDialog();
        }
    }

    /**
     * On changes: a subscription given later by the layout (refreshed after the end of the trial) is checked too
     */
    ngOnChanges(changes: SimpleChanges): void {
        if (changes['subscription'] && !changes['subscription'].firstChange) {
            this.checkAndShowTrialEndDialog();
        }
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Open the trial-end plan selection dialog when the server says it is due: first connection of the owner after the
     * end of a trial without Stripe, whether the hourly job or this connection ended it. The server hands it out once in
     * total (show is true for one call only), whatever the device or the tab.
     */
    private checkAndShowTrialEndDialog(): void {
        if (this._trialEndDialogShown || !this.subscription?.trialEndPlanChoiceDue) {
            return;
        }
        this._trialEndDialogShown = true;

        // Never on the Subscription page, which offers the plan choice itself: the "Choose my plan" button of the trial
        // e-mails lands there with ?upgrade=true and opens the plan selector, and a Stripe checkout comes back there. One
        // plan choice on screen, and the window is not asked for: the server keeps it due (not used up) for a later
        // connection on another page, while the Free plan of the trial end is still the current subscription
        if (this.isSubscriptionPage()) {
            return;
        }

        this._userService.user$
            .pipe(
                take(1),
                switchMap((user) => this._subscriptionService.claimTrialEndPlanChoice(user.partnerId)),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe({
                next: (response) => {
                    if (response?.show) {
                        // Small delay to let the UI settle
                        setTimeout(() => {
                            this.openTrialEndPlanSelection();
                        }, 1000);
                    }
                },
                // No window this time; unless the server recorded it, it stays due for the next connection
                error: () => {}
            });
    }

    /**
     * Whether the page shown is the Subscription page (at the first connection, the address being loaded)
     */
    private isSubscriptionPage(): boolean {
        const subscriptionPath = `/${PathEnum.APP}/${PathEnum.APP_PARTNER_SETTINGS}/${PathEnum.APP_PARTNER_SETTINGS_SUBSCRIPTION}`;
        return this._router.url.split(/[?#]/)[0] === subscriptionPath;
    }

    /**
     * Open the trial-end plan selection dialog
     */
    private openTrialEndPlanSelection(): void {
        const dialogRef = this._matDialog.open(TrialEndPlanSelectionComponent, {
            width: '900px',
            maxWidth: '95vw',
            disableClose: true,
            data: { plans: null } // Use default plans
        });

        dialogRef.afterClosed().subscribe((selectedPlanCode: string) => {
            if (selectedPlanCode) {
                this.handlePlanSelection(selectedPlanCode);
            }
        });
    }

    /**
     * Handle the plan selection from the trial-end dialog
     */
    private handlePlanSelection(planCode: string): void {
        if (planCode === PlanEnum.FREE) {
            // Stay on free - no action needed, just close dialog
            return;
        }

        // For LITE or PRO, redirect to subscription settings to complete checkout
        this.navigateToSubscription();
    }
}
